import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Edit2, Download, CheckSquare, Square } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Table from '../components/Table';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import { FF, ErrMsg } from '../components/FormField';
import { FilterBar, SearchInput, FilterSelect, PageHeader } from '../components/FilterBar';
import DateRangePicker from '../components/DateRangePicker';
import CompanyDeptFilter from '../components/CompanyDeptFilter';
import SearchableSelect from '../components/SearchableSelect';

const MILESTONES = [
  { key: 'hr_informed',      label: 'HR Informed' },
  { key: 'finance_informed', label: 'Finance Informed' },
  { key: 'quotation_sent',   label: 'Quotation Sent to Finance' },
  { key: 'tech_report_sent', label: 'Tech Report Sent' },
  { key: 'claim_received',   label: 'Claim Received' },
];

function MilestoneBar({ claim }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {MILESTONES.map(m => (
        <span key={m.key} className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded"
          style={{ background: claim[m.key] ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.04)', color: claim[m.key] ? '#34d399' : '#334155', border: `1px solid ${claim[m.key] ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.06)'}` }}>
          {claim[m.key] ? <CheckSquare size={10}/> : <Square size={10}/>} {m.label}
        </span>
      ))}
    </div>
  );
}

function statusColor(s) {
  if (s === 'completed') return '#34d399';
  if (s === 'in_progress') return '#60a5fa';
  return '#f59e0b';
}

export default function Insurance() {
  const { isIT, isAdmin, isDirector, isFinance } = useAuth();
  const showCompanyFilter = isAdmin || isDirector || isFinance;
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [pages, setPages] = useState(1); const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMilestone, setFilterMilestone] = useState('');
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');
  const [filterCompany, setFilterCompany] = useState([]); const [filterDept, setFilterDept] = useState(null);
  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const [assets, setAssets] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterMilestone) params[filterMilestone] = 'true';
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      if (filterCompany.length) params.company_id = filterCompany.join(',');
      if (filterDept) params.department_id = filterDept;
      const r = await api.get('/insurance-claims', { params });
      setRows(r.data.data || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, search, filterStatus, filterMilestone, dateFrom, dateTo, filterCompany, filterDept]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/assets', { params: { limit: 2000 } }).then(r => setAssets(r.data.data || [])).catch(() => {});
  }, []);

  const exportCSV = async () => {
    const params = { limit: 2000 };
    if (search) params.search = search; if (filterStatus) params.status = filterStatus;
    if (filterMilestone) params[filterMilestone] = 'true';
    if (dateFrom) params.from = dateFrom; if (dateTo) params.to = dateTo;
    const r = await api.get('/insurance-claims', { params });
    const data = r.data.data || [];
    const headers = ['Asset #','Brand/Model','Informed Date','Informed Person','Status','HR Informed','Finance Informed','Quotation Sent','Tech Report Sent','Claim Received','Remarks'];
    const rows2 = data.map(c => [
      c.asset?.asset_number||'', `${c.asset?.brand||''} ${c.asset?.model||''}`.trim(),
      c.informed_date||'', c.informed_person||'', c.status||'',
      c.hr_informed?'Yes':'No', c.finance_informed?'Yes':'No', c.quotation_sent?'Yes':'No',
      c.tech_report_sent?'Yes':'No', c.claim_received?'Yes':'No', c.remarks||''
    ].map(v => `"${String(v).replace(/"/g,'""')}"`));
    const csv = [headers.join(','), ...rows2.map(r => r.join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `insurance_claims_${dateFrom||'all'}_${dateTo||'all'}.csv`;
    a.click();
  };

  const handleSave = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (selected) {
        await api.put(`/insurance-claims/${selected.id}`, form);
      } else {
        await api.post('/insurance-claims', form);
      }
      setModal(null); setSelected(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  // Toggle a milestone on an existing claim directly from the table
  const toggleMilestone = async (claim, key) => {
    const updated = { [key]: !claim[key] };
    await api.put(`/insurance-claims/${claim.id}`, updated);
    load();
  };

  const columns = [
    { header: 'Asset', render: r => r.asset ? (
      <div>
        <div className="font-mono text-xs font-semibold" style={{color:'#60a5fa'}}>{r.asset.asset_number}</div>
        <div className="text-xs" style={{color:'#475569'}}>{r.asset.brand} {r.asset.model}</div>
      </div>
    ) : <span style={{color:'#334155'}}>—</span> },
    { header: 'Informed Date', render: r => <span className="text-xs" style={{color:'#94a3b8'}}>{r.informed_date||'—'}</span> },
    { header: 'Informed Person', render: r => <div><div className="text-xs" style={{color:'#e2e8f0'}}>{r.informed_person||'—'}</div>{r.informed_person_email && <div className="text-xs" style={{color:'#475569'}}>{r.informed_person_email}</div>}</div> },
    { header: 'Status / Amount', render: r => (
      <div>
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{background:`${statusColor(r.status)}20`,color:statusColor(r.status)}}>
          {r.status === 'completed' ? 'Claim Received' : r.status === 'in_progress' ? 'In Progress' : 'Pending'}
        </span>
        {r.claim_received && r.claim_received_amount && (
          <div className="text-xs mt-1 font-semibold" style={{color:'#34d399'}}>
            LKR {Number(r.claim_received_amount).toLocaleString()}
          </div>
        )}
      </div>
    )},
    { header: 'Progress', render: r => <MilestoneBar claim={r}/> },
    { header: '', render: r => isIT && (
      <button onClick={() => { setSelected(r); setForm({...r}); setError(''); setModal('form'); }} className="btn-ghost px-2 py-1 text-xs"><Edit2 size={11}/></button>
    )}
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="Insurance Claims" count={total}>
        <div className="flex items-center gap-2 flex-wrap">
          {showCompanyFilter && <CompanyDeptFilter companyId={filterCompany} deptId={filterDept} onChange={(c,d) => { setFilterCompany(c); setFilterDept(d); setPage(1); }}/>}
          <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13}/></button>
          {isIT && <button onClick={exportCSV} className="btn-ghost p-2" title="Export CSV"><Download size={13}/></button>}
          {isIT && <button onClick={() => { setSelected(null); setForm({}); setError(''); setModal('form'); }} className="btn-primary"><Plus size={13}/> Add Claim</button>}
        </div>
      </PageHeader>

      <FilterBar hasFilters={!!(search||filterStatus||filterMilestone||dateFrom||dateTo)} onClear={() => { setSearch(''); setFilterStatus(''); setFilterMilestone(''); setDateFrom(''); setDateTo(''); setPage(1); }}>
        <SearchInput value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search asset, person, remarks..."/>
        <FilterSelect value={filterStatus} onChange={v => { setFilterStatus(v); setPage(1); }} placeholder="All Statuses"
          options={[{value:'pending',label:'Pending'},{value:'in_progress',label:'In Progress'},{value:'completed',label:'Claim Received'}]}/>
        <FilterSelect value={filterMilestone} onChange={v => { setFilterMilestone(v); setPage(1); }} placeholder="Filter by Milestone"
          options={MILESTONES.map(m => ({ value: m.key, label: m.label }))}/>
        <DateRangePicker from={dateFrom} to={dateTo} onChange={(f,t) => { setDateFrom(f); setDateTo(t); setPage(1); }}/>
      </FilterBar>

      <Table columns={columns} data={rows} loading={loading} page={page} pages={pages} total={total} onPage={setPage}/>

      {modal === 'form' && (
        <Modal title={selected ? `Edit Claim` : 'Add Insurance Claim'} onClose={() => { setModal(null); setSelected(null); }} size="lg">
          <form onSubmit={handleSave} className="space-y-4">
            <ErrMsg msg={error}/>

            {/* Asset + basic details */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Asset *</label>
                <SearchableSelect required value={form.asset_id||''} onChange={v=>setForm({...form,asset_id:v})} placeholder="Select asset..."
                  options={assets.map(a=>({value:a.id,label:`${a.asset_number} — ${a.brand} ${a.model}${a.serial_number?` (S/N: ${a.serial_number})`:''}`}))}/>
              </div>
              <FF label="Informed Date *" name="informed_date" type="date" form={form} setForm={setForm} required/>
              <FF label="Informed Person Name *" name="informed_person" form={form} setForm={setForm} required placeholder="Name of finance/HR person informed"/>
              <div className="col-span-2">
                <FF label="Informed Person Email" name="informed_person_email" type="email" form={form} setForm={setForm} placeholder="email@clsynergy.com — notification email will be sent on add"/>
                <div className="text-xs mt-1" style={{color:'#334155'}}>If entered, a claim notification email will be sent to this person on save, with asset details and instructions to proceed with the insurance company.</div>
              </div>
            </div>
            <FF label="Remarks / Incident Details" name="remarks" form={form} setForm={setForm} rows={3} placeholder="Incident details, nature of damage, claim reason..."/>

            {/* Milestone checkboxes */}
            <div className="rounded-xl p-4" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.07)'}}>
              <div className="text-xs font-semibold mb-3" style={{color:'#94a3b8'}}>Claim Progress</div>
              <div className="space-y-2.5">
                {MILESTONES.map((m, i) => {
                  const prevDone = i === 0 || form[MILESTONES[i-1].key];
                  return (
                    <label key={m.key} className="flex items-center gap-3 cursor-pointer rounded-lg px-3 py-2"
                      style={{background: form[m.key] ? 'rgba(16,185,129,0.08)' : 'transparent', border: `1px solid ${form[m.key] ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.04)'}` }}>
                      <input type="checkbox"
                        checked={!!form[m.key]}
                        onChange={e => setForm({ ...form, [m.key]: e.target.checked })}
                        className="w-4 h-4 accent-emerald-500"/>
                      <div>
                        <div className="text-sm font-medium" style={{color: form[m.key] ? '#34d399' : '#e2e8f0'}}>{m.label}</div>
                        {m.key === 'finance_informed' && <div className="text-xs" style={{color:'#475569'}}>Ticking this moves claim to In Progress</div>}
                        {m.key === 'claim_received' && <div className="text-xs" style={{color:'#475569'}}>Ticking this marks claim as Completed</div>}
                      </div>
                    </label>
                  );
                })}
              </div>

              {/* Claim received amount — shown as sibling outside label, not nested inside it */}
              {form.claim_received && (
                <div className="mt-3 p-3 rounded-lg" style={{background:'rgba(16,185,129,0.08)',border:'1px solid rgba(16,185,129,0.2)'}}>
                  <label className="block text-xs font-semibold mb-1.5" style={{color:'#34d399'}}>Amount Received (LKR)</label>
                  <input
                    type="number" step="0.01" min="0"
                    value={form.claim_received_amount||''}
                    onChange={e => setForm({...form, claim_received_amount: e.target.value})}
                    className="input-dark" placeholder="Enter the claim amount received..."
                  />
                </div>
              )}

              {/* Live status preview */}
              <div className="mt-3 text-xs" style={{color:'#475569'}}>
                Status will be set to:{' '}
                <span className="font-semibold" style={{color: statusColor(form.claim_received ? 'completed' : form.finance_informed ? 'in_progress' : 'pending')}}>
                  {form.claim_received ? `Claim Received (Completed)${form.claim_received_amount ? ` — LKR ${Number(form.claim_received_amount).toLocaleString()}` : ''}` : form.finance_informed ? 'In Progress' : 'Pending'}
                </span>
              </div>
            </div>

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving ? 'Saving...' : selected ? 'Save Changes' : 'Add Claim'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
